import Cookies from 'js-cookie';

import {
  buildLeadEvent,
  mapConsentToSignals,
  mintEventId,
  pushLead,
  readClickIds,
  type LeadFunnel
} from './dataLayer';
import { sendCapiEvent } from '@lib/api/capi-shim';
import { ConsentManager } from '@lib/consent';

/**
 * FFH-990 — fire a Lead on BOTH rails (Pattern A, architecture §2.1). Mints ONE
 * `event_id` and passes it to the dataLayer push (read by the GTM pixel) AND the
 * CAPI shim, so the pixel and server-side CAPI events dedup on a single id.
 *
 * Anonymous identity rides the `_aid` first-party cookie (dedup-contract §5):
 * the shim forwards it to the backend via `credentials: 'include'`, and we
 * mirror it into the pixel push's `externalId` so both rails carry the same id.
 * It may be absent at reveal (the funnel hasn't captured an email yet); that
 * only weakens external-id match quality, never the event_id dedup.
 *
 * Fail-soft: an analytics failure must never break the funnel result reveal.
 */
export const fireLeadEvent = (
  funnel: LeadFunnel,
  funnelResult?: string,
  params?: { email?: string }
): void => {
  if (typeof window === 'undefined') return;

  // Idempotency (FFH-359): one Lead per funnel per browser-tab session. The
  // reveal can re-render and the funnel can be retried / navigated back to and
  // re-mounted, which would otherwise re-fire and inflate Lead volume.
  // sessionStorage (not localStorage) so a genuinely new session can lead
  // again; fail-soft so unavailable storage (private mode / webview) never
  // blocks the fire.
  try {
    const idempotencyKey = `ffh_lead_${funnel}`;
    if (window.sessionStorage.getItem(idempotencyKey)) return;
    window.sessionStorage.setItem(idempotencyKey, 'true');
  } catch {
    // Storage unavailable — proceed without the guard rather than dropping the Lead.
  }

  try {
    const eventId = mintEventId();
    const consent = mapConsentToSignals(
      new ConsentManager().getCurrentConsent()
    );
    const sourceUrl = window.location.href;

    pushLead(
      buildLeadEvent({
        eventId,
        funnel,
        funnelResult,
        email: params?.email,
        externalId: Cookies.get('_aid'),
        click: readClickIds(),
        sourceUrl,
        referrer: document.referrer || undefined,
        consent
      })
    );

    void sendCapiEvent({ eventName: 'lead', eventId, sourceUrl, consent });
  } catch {
    // Swallow — analytics must never break the funnel reveal.
  }
};
